import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { cn } from 'app/lib/utils'
import type { RelatedPageLinksEntry } from '@/utils/relatedPageLinks'
import { buildRelatedPageLinksIntro } from '@/utils/relatedPageLinksText'

const PANEL_VISIBLE_CLASSES =
  'group-hover/related-links:pointer-events-auto group-hover/related-links:opacity-100 group-hover/related-links:delay-0 group-focus-within/related-links:pointer-events-auto group-focus-within/related-links:opacity-100 group-focus-within/related-links:delay-0'

type RelatedPageLinksProps = {
  entry?: RelatedPageLinksEntry | null
  className?: string
}

export function RelatedPageLinks({ entry, className }: RelatedPageLinksProps) {
  if (!entry || entry.links.length === 0) return null

  return (
    <div className={cn('not-prose group/related-links', className)} data-markdown-ignore>
      <button
        type="button"
        aria-label="Related pages"
        className="flex size-6 items-center justify-center rounded text-[var(--l3-foreground)] transition-colors hover:text-[var(--l1-foreground)] focus-visible:text-[var(--l1-foreground)]"
      >
        <Info size={16} />
      </button>
      <div
        className={`pointer-events-none absolute right-0 z-50 w-80 max-w-[calc(100vw-2rem)] pt-2 opacity-0 transition-[opacity,pointer-events] delay-200 [transition-behavior:allow-discrete] ${PANEL_VISIBLE_CLASSES}`}
      >
        <div className="rounded-md border border-[var(--l2-border)] bg-[var(--l2-background)] px-3 py-2 text-left text-xs leading-snug text-[var(--l1-foreground)] shadow-[0_6px_12px_0_color-mix(in_srgb,var(--base-black)_20%,transparent)]">
          <p className="m-0">{buildRelatedPageLinksIntro(entry.title)}</p>
          <ol className="mb-0 mt-2 list-decimal space-y-1 pl-4">
            {entry.links.map((url) => (
              <li key={url}>
                <a href={url} className="break-all underline underline-offset-2">
                  {url}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  )
}

export default function RelatedPageLinksTitle({
  entry,
  children,
}: {
  entry?: RelatedPageLinksEntry | null
  children: ReactNode
}) {
  if (!entry || entry.links.length === 0) return <>{children}</>

  return (
    <div className="relative before:[&>h1]:float-right before:[&>h1]:h-6 before:[&>h1]:w-8">
      {children}
      <RelatedPageLinks entry={entry} className="absolute right-0 top-1.5" />
    </div>
  )
}
